import React, { useEffect, useRef, useState } from "react";
import { servicesData } from "@/data";
import { ChevronLeft, ChevronRight } from "lucide-react";





export const ServicesSection: React.FC = () => {
  const [activeBg, setActiveBg] = useState<string>("/explore/services-default.png");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  // Fixed card width
  const CARD_WIDTH = 325; // Fixed width for each card
  const CARD_GAP = 24; // Gap between cards

  // Get cards to display based on screen size - exact breakpoints
  const getCardsPerView = () => {
    if (typeof window === "undefined") return 4;
    const width = window.innerWidth;

    // Calculate required width for each configuration
    // Formula: (cards * cardWidth) + ((cards - 1) * gap) + padding
    const width4Cards = 4 * CARD_WIDTH + 3 * CARD_GAP + 96; // 1472px
    const width3Cards = 3 * CARD_WIDTH + 2 * CARD_GAP + 96; // 1116px
    const width2Cards = 2 * CARD_WIDTH + 1 * CARD_GAP + 96; // 800px

    if (width >= width4Cards) return 4;
    if (width >= width3Cards) return 3;
    if (width >= width2Cards) return 2;
    return 1;
  };

  const [cardsPerView, setCardsPerView] = useState(getCardsPerView());

  // Calculate container width based on cards per view
  const getContainerWidth = () => {
    return cardsPerView * CARD_WIDTH + (cardsPerView - 1) * CARD_GAP;
  };
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Create extended array for infinite scroll
  const extendedServices = [
    ...servicesData.services,
    ...servicesData.services,
    ...servicesData.services,

  ];

  // Intersection Observer to detect if carousel is in viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setCardsPerView(getCardsPerView());
      setCurrentIndex(0);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reset position when reaching end of first set
  useEffect(() => {
    if (currentIndex >= servicesData.services.length && !isTransitioning) {
      setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(0);
      }, 50);
    }
  }, [currentIndex, isTransitioning]);

  // Auto-slide carousel - only when in view and not paused
  useEffect(() => {
    if (!isPaused && isInView) {
      const interval = setInterval(() => {
        nextSlide();
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [isPaused, isInView, currentIndex]);

  const nextSlide = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const prevSlide = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      if (currentIndex === 0) {
        setCurrentIndex(servicesData.services.length);
        setTimeout(() => {
          setCurrentIndex(servicesData.services.length - 1);
        }, 50);
      } else {
        setCurrentIndex((prev) => prev - 1);
      }
    }
  };

  return (
    <div id="services" className="scroll-mt-24" ref={sectionRef}>
      {/* Header */}
      <h2 className="text-3xl text-center font-semibold font-poppins text-[#57c0af] ml-3 mt-32 mb-9 text-left">
        <span className="text-white text-3xl font-medium">Nuqi</span>{" "}
        <span className="text-[#57c0af] text-3xl font-medium">
          Prive Offerings
        </span>
      </h2> 

      <section
        className="py-12 relative overflow-hidden transition-all duration-700 mt-18"
        style={{
          backgroundImage: `url(${activeBg})`,
          backgroundSize: "cover",  
          backgroundPosition: "center",
        }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/35 pointer-events-none"></div>
        <div className="relative z-10">
          {/* Carousel Container */}
          <div
            className="mx-auto px-6 relative"
            style={{ maxWidth: `${getContainerWidth() + 96}px` }}
          >
            {/* Navigation Buttons */}
            <button
              onClick={prevSlide}
              className="absolute -left-4 top-1/2 -translate-y-1/2 z-20 bg-[#57c0af]/20 hover:bg-[#57c0af]/40 backdrop-blur-sm border border-[#57c0af]/40 p-3 rounded-full transition-all duration-300 hover:scale-110"
              aria-label="Previous slide"
            >
              <ChevronLeft size={24} className="text-[#57c0af]" />

            </button>

            <button
              onClick={nextSlide}
              className="absolute -right-4 top-1/2 -translate-y-1/2 z-20 bg-[#57c0af]/20 hover:bg-[#57c0af]/40 backdrop-blur-sm border border-[#57c0af]/40 p-3 rounded-full transition-all duration-300 hover:scale-110"
              aria-label="Next slide"
            >
              <ChevronRight size={24} className="text-[#57c0af]" />

            </button>

            {/* Cards */}
            <div className="overflow-visible px-6">
              <div className="relative overflow-hidden">
                <div
                  className={`flex ${isTransitioning
                      ? "transition-transform duration-500 ease-in-out"
                      : ""
                    }`}
                  style={{
                    transform: `translateX(-${currentIndex * (CARD_WIDTH + CARD_GAP)
                      }px)`,
                    gap: `${CARD_GAP}px`,
                  }}
                  onTransitionEnd={() => setIsTransitioning(false)}
                >
                  {extendedServices.map((service, index) => (
                    <div
                      key={`service-${index}`}
                      onMouseEnter={() =>
                        setActiveBg(
                          servicesData.services[index % servicesData.services.length].bgimage || "/explore/services-default.png"
                        )
                      }
                      onMouseLeave={() =>
                        setActiveBg("/explore/services-default.png")
                      }
                      className="group flex flex-col justify-between bg-black/50 backdrop-blur-sm border border-white/10 rounded-2xl p-1 hover:translate-1 hover:scale-[1] hover:w-full card-hover-glow cursor-pointer flex-shrink-0 transition-all duration-300"
                      style={{
                        ["--card-theme" as any]: service.themeColor || "#57c0af",
                        width: `${CARD_WIDTH}px`,
                        minHeight: "200px",
                      }}
                    >
                      <div className="relative z-10 flex flex-col justify-between h-[40]">
                        <div className="mr-3 items-center m-3 justify-between mt-1 p-6 text-white transition-shadow duration-300 ease-in-out" style={{ background: 'transparent' }}>
                          <div style={{ background: 'transparent', display: 'flex', justifyContent: 'center' }}>
                            <img
                              src={servicesData.services[index % servicesData.services.length].mediaSrc}
                              alt={service.title}
                              className=" h-32 object-contain rounded-md"
                            />
                          </div>
                      
                          {/* Title */}
                          <h3
                            className="text-xl font-normal mb-6 mt-4 leading-tight"
                            style={{ color: service.themeColor || "#57c0af" }}
                          >
                            {service.title}
                          </h3>

                          {/* Description */}
                          {service.description && (
                            <p className="text-sm text-[#CCCCCC] font-light leading-relaxed whitespace-pre-line group-hover:text-white transition-colors duration-300">
                              {service.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Carousel Indicators */}
            <div className="flex justify-center gap-2 mt-8">
              {servicesData.services.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setIsTransitioning(true);
                    setCurrentIndex(index);
                  }}
                  className={`h-2 rounded-full transition-all duration-300 ${currentIndex % servicesData.services.length === index
                      ? "w-8 bg-[#57c0af]"
                      : "w-2 bg-white/30 hover:bg-white/50"
                    }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
        <style>{`
         .card-hover-glow:hover {
            border-color: var(--card-theme);
            box-shadow: 0 10px 25px -12px var(--card-theme);
         }
       `}</style>
      </section>
    </div>
  );
};

// Note: You need to import ChevronLeft and ChevronRight from lucide-react or similar.
// Assuming servicesData.services has properties like title, description, icon, bgimage, themeColor.

